import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

// Footer date slider. Mirrors ZoomSlider's bubble - drag state lives HERE so
// dragging re-renders only this component, and the bubble portals to
// document.body so no ancestor overflow can clip it.
//
// The chart still scrolls LIVE while dragging, but the parent's scroll-percent
// state is only written on release. That split is the whole point of this
// component:
//
//   onScrub  - every step. The parent may ONLY write scrollLeft imperatively.
//              No setState. The chart reads scroll percent nowhere, so a
//              re-render here repaints every bar and every dependency arrow for
//              a value nothing consumes - which is what made dragging jerky
//              while the native scrollbar (pure compositor scroll) was smooth.
//   onCommit - on release. The parent stores the final percent.
//
// Because the parent's `value` is frozen during a drag, the live position is
// held here and `value` is used again as soon as the parent catches up.
//
// `step` is the percent ONE DAY represents, supplied by the caller because only
// it knows px-per-day and the scroll width. It must not be left to the HTML
// default of 1: that steps 1% of the whole range, which on a chart padded a
// year forward is about four days a notch.
const DateSlider = ({ value, onScrub, onCommit, formatValue, step = 1 }) => {
    const [dragging, setDragging] = useState(false);
    const [live, setLive] = useState(null);   // null = not dragging, follow `value`
    const inputRef = useRef(null);

    // The parent has caught up (on commit, or an external jump such as the date
    // picker), so hand control back to it.
    useEffect(() => setLive(null), [value]);

    const current = live ?? value;

    const stop = () => {
        setDragging(false);
        if (live !== null) onCommit?.(live);
    };

    const rect = inputRef.current?.getBoundingClientRect();
    const label = dragging && formatValue ? formatValue(current) : null;

    return (
        <div className="relative flex items-center flex-1">
            {label && rect && createPortal(
                <div
                    className="px-1.5 py-0.5 text-xs font-medium rounded bg-gray-800 text-white pointer-events-none whitespace-nowrap"
                    style={{
                        position: 'fixed',
                        top: rect.top - 28,
                        left: rect.left + (current / 100) * rect.width - 32,
                        zIndex: 9999
                    }}
                >
                    {label}
                </div>,
                document.body
            )}
            <input
                ref={inputRef}
                type="range"
                min="0"
                max="100"
                step={step}
                value={current}
                onChange={(e) => {
                    const percent = parseFloat(e.target.value);
                    setDragging(true);
                    setLive(percent);
                    onScrub?.(percent);
                }}
                onMouseUp={stop}
                onTouchEnd={stop}
                onKeyUp={stop}
                onBlur={stop}
                className="gantt-zoom-slider flex-1 cursor-pointer"
            />
        </div>
    );
};

export default DateSlider;
